import { useEffect, useMemo, useState } from "react";
import { addMonths, endOfMonth, startOfMonth } from "date-fns";
import {
  DemandForecastCalendarBucket,
  DemandForecastCalendarPeriod,
  DemandForecastCalendarResponseSchema,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocationSelector } from "@/components/LocationSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useDemandForecastsStore } from "./demandForecastsStore";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  formatDateInput,
  formatMoney,
  formatQuantityWithUnit,
  parseDateInput,
} from "./utils";
import {
  buildPeriodDemand,
  getPercentDelta,
  getPreviousDateRange,
  getPrimaryVolume,
  getQuantityByUnit,
  summarizeDemand,
} from "./demandForecastCalendarUtils";
import { DeltaPill } from "./DeltaPill";
import { Stat } from "./Stat";
import { DemandStackedBar } from "./DemandStackedBar";
import { PeriodBreakdown } from "./PeriodBreakdown";
import { useCountUp } from "./useCountUp";

export function DemandForecastCalendarView({
  refreshToken = 0,
}: {
  refreshToken?: number;
}) {
  const { calendarBuckets, calendarLoading, calendarError, fetchCalendar } =
    useDemandForecastsStore();

  const [bucket, setBucket] = useState<DemandForecastCalendarBucket>("QUARTER");
  const [locationId, setLocationId] = useState<string | null>(null);
  const [previousCalendarBuckets, setPreviousCalendarBuckets] = useState<
    DemandForecastCalendarPeriod[]
  >([]);
  const [startDate, setStartDate] = useState(
    formatDateInput(startOfMonth(new Date())),
  );
  const [endDate, setEndDate] = useState(
    formatDateInput(endOfMonth(addMonths(new Date(), 5))),
  );

  const fetchParams = useMemo(
    () =>
      startDate && endDate
        ? {
            startDate: parseDateInput(startDate),
            endDate: parseDateInput(endDate),
            locationId: locationId ?? undefined,
            bucket,
          }
        : null,
    [bucket, endDate, locationId, startDate],
  );
  const previousDateRange = useMemo(
    () => getPreviousDateRange(startDate, endDate),
    [endDate, startDate],
  );

  const periodDemand = useMemo(
    () => buildPeriodDemand(calendarBuckets, startDate, endDate, bucket),
    [bucket, calendarBuckets, endDate, startDate],
  );
  const previousPeriodDemand = useMemo(
    () =>
      previousDateRange
        ? buildPeriodDemand(
            previousCalendarBuckets,
            previousDateRange.startDate,
            previousDateRange.endDate,
            bucket,
          )
        : [],
    [bucket, previousCalendarBuckets, previousDateRange],
  );
  const maxWeightedRevenue = useMemo(
    () =>
      periodDemand.reduce(
        (max, period) => Math.max(max, period.weightedRevenue),
        0,
      ),
    [periodDemand],
  );
  const demandSummary = useMemo(
    () => summarizeDemand(periodDemand),
    [periodDemand],
  );
  const previousDemandSummary = useMemo(
    () => summarizeDemand(previousPeriodDemand),
    [previousPeriodDemand],
  );
  const primaryVolume = getPrimaryVolume({
    ...demandSummary,
    quantityTotals: demandSummary.weightedQuantityTotals,
  });
  const previousPrimaryVolume = primaryVolume
    ? getQuantityByUnit(
        previousDemandSummary.weightedQuantityTotals,
        primaryVolume.quantityUnit,
      )
    : 0;
  const bucketLabel =
    bucket === "MONTH" ? "month" : bucket === "QUARTER" ? "quarter" : "week";

  useEffect(() => {
    if (fetchParams) {
      fetchCalendar(fetchParams);
    }
  }, [fetchCalendar, fetchParams, refreshToken]);

  useEffect(() => {
    if (!previousDateRange) {
      setPreviousCalendarBuckets([]);
      return;
    }

    let cancelled = false;
    const fetchPreviousCalendar = async () => {
      try {
        const searchParams = new URLSearchParams({
          startDate: parseDateInput(previousDateRange.startDate).toISOString(),
          endDate: parseDateInput(previousDateRange.endDate).toISOString(),
          bucket,
        });
        if (locationId) {
          searchParams.set("locationId", locationId);
        }

        const response = await fetchApi(
          `demand-forecasts/calendar?${searchParams.toString()}`,
          "GET",
        );
        if (!response.ok || !response.data) {
          if (!cancelled) {
            setPreviousCalendarBuckets([]);
          }
          return;
        }

        const data = DemandForecastCalendarResponseSchema.parse(response.data);
        if (!cancelled) {
          setPreviousCalendarBuckets(data.buckets);
        }
      } catch (error) {
        captureException(error as Error);
        if (!cancelled) {
          setPreviousCalendarBuckets([]);
        }
      }
    };

    fetchPreviousCalendar();

    return () => {
      cancelled = true;
    };
  }, [bucket, locationId, previousDateRange, refreshToken]);

  const animatedPipeline = useCountUp(demandSummary.weightedRevenue);

  const formatTick = (value: number) =>
    value >= 1000
      ? `$${(value / 1000).toLocaleString("en-US", {
          maximumFractionDigits: 1,
        })}k`
      : `$${Math.round(value)}`;

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-6 overflow-x-hidden">
      <header className="min-w-0 border-b border-border pb-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
          Demand forecast
        </p>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-3xl font-semibold tabular-nums tracking-tight sm:text-[2.5rem] sm:leading-none">
            {formatMoney(animatedPipeline)}
          </span>
          <DeltaPill
            delta={getPercentDelta(
              demandSummary.weightedRevenue,
              previousDemandSummary.weightedRevenue,
            )}
          />
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Weighted pipeline across {periodDemand.length} {bucketLabel}
          {periodDemand.length === 1 ? "" : "s"} · vs. prior period
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-muted/20 p-3">
        <div className="min-w-0 space-y-1.5">
          <Label>Bucket</Label>
          <Select
            value={bucket}
            onValueChange={(value) =>
              setBucket(value as DemandForecastCalendarBucket)
            }
          >
            <SelectTrigger className="w-full lg:w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MONTH">Month</SelectItem>
              <SelectItem value="QUARTER">Quarter</SelectItem>
              <SelectItem value="WEEK">Week</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label>Start</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            className="w-full lg:w-[150px]"
          />
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label>End</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            className="w-full lg:w-[150px]"
          />
        </div>
        <div className="min-w-0 flex-1 space-y-1.5 lg:min-w-[240px] lg:flex-none">
          <Label>Location</Label>
          <LocationSelector
            currentlySelectedId={locationId}
            onSelectId={setLocationId}
            placeholder="All locations"
          />
        </div>
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          onClick={() => fetchParams && fetchCalendar(fetchParams)}
          disabled={!fetchParams}
        >
          Refresh
        </Button>
      </div>

      {calendarError && (
        <div className="text-sm text-destructive">{calendarError}</div>
      )}

      {calendarLoading ? (
        <div className="space-y-3 overflow-hidden">
          <Skeleton className="h-[380px] w-full" />
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-36 w-full" />
            ))}
          </div>
        </div>
      ) : periodDemand.every((period) => period.weightedRevenue === 0) ? (
        <div className="flex min-h-[240px] items-center justify-center rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
          No forecast demand in this range
        </div>
      ) : (
        <div className="w-full min-w-0 max-w-full space-y-6 overflow-x-hidden">
          <div className="grid grid-cols-1 divide-y divide-border overflow-hidden rounded-lg border bg-card sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <Stat
              label="Total revenue"
              value={formatMoney(demandSummary.totalRevenue)}
              delta={getPercentDelta(
                demandSummary.totalRevenue,
                previousDemandSummary.totalRevenue,
              )}
            />
            <Stat
              label="Weighted volume"
              value={
                primaryVolume
                  ? formatQuantityWithUnit(
                      String(primaryVolume.quantity),
                      primaryVolume.quantityUnit,
                    )
                  : "—"
              }
              delta={
                primaryVolume
                  ? getPercentDelta(
                      primaryVolume.quantity,
                      previousPrimaryVolume,
                    )
                  : null
              }
            />
            <Stat
              label="Avg. probability"
              value={`${Math.round(demandSummary.avgProbability)}%`}
              delta={getPercentDelta(
                demandSummary.avgProbability,
                previousDemandSummary.avgProbability,
              )}
            />
          </div>

          <section className="min-w-0 overflow-hidden rounded-lg border bg-card p-4 text-card-foreground sm:p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h2 className="text-base font-semibold tracking-tight">
                Weighted demand by {bucketLabel}
              </h2>
              <span className="text-xs text-muted-foreground">
                Stacked by opportunity · weighted by probability
              </span>
            </div>

            <div className="mt-5 flex gap-2 sm:gap-3">
              <div className="hidden w-12 shrink-0 pt-[44px] sm:block">
                <div className="relative h-[280px]">
                  <span className="absolute right-1 top-0 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground">
                    {formatTick(maxWeightedRevenue)}
                  </span>
                  <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground">
                    {formatTick(maxWeightedRevenue / 2)}
                  </span>
                  <span className="absolute bottom-0 right-1 translate-y-1/2 text-[10px] tabular-nums text-muted-foreground">
                    $0
                  </span>
                </div>
              </div>

              <div className="relative min-w-0 flex-1 overflow-x-auto">
                <div
                  className="pointer-events-none absolute inset-x-0 z-0 duration-700 animate-in fade-in"
                  style={{ top: 44, height: 280 }}
                >
                  <div className="absolute inset-x-0 top-0 border-t border-dashed border-border/60" />
                  <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-border/60" />
                  <div className="absolute inset-x-0 bottom-0 border-t border-border" />
                </div>
                <div className="relative z-10 grid min-w-full grid-flow-col auto-cols-[minmax(104px,1fr)] items-end gap-3 sm:auto-cols-[minmax(132px,1fr)] sm:gap-4">
                  {periodDemand.map((period, index) => (
                    <DemandStackedBar
                      key={period.key}
                      period={period}
                      maxWeightedRevenue={maxWeightedRevenue}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Period breakdown
            </h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {periodDemand
                .filter((period) => period.weightedRevenue > 0)
                .map((period) => (
                  <PeriodBreakdown key={period.key} period={period} />
                ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
